/*
  Veya on Atlas: keep the priced add-to-cart label ("Add to cart · AED 179")
  in step with the chosen pack.

  The app-served Atlas JS rewrites the button on every variant change
  (toggleAddButton writes window.variantStrings.addToCart into the first
  <span> of the button, which is the hidden "Added" span). This element
  restores both spans from a map rendered server-side in
  snippets/a-buy-buttons-r3.liquid, so money is never formatted in JS.

  Rules:
  - never enable a button and never touch it while it is disabled, busy
    (aria-disabled / .loading) or showing text we did not write;
  - an unavailable pack gets the "Sold out" text, and only in place of one
    of our priced labels;
  - the quantity stepper's subtotal ("AED 358.00") loses a trailing ".00".
*/
if (!customElements.get('veya-atlas-offer')) {
  customElements.define('veya-atlas-offer', class VeyaAtlasOffer extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.veya-buy-buttons') || this.parentElement;
      if (!this.root) return;
      this.form = this.root.querySelector('form[data-type="add-to-cart-form"]') || this.root.querySelector('form');
      this.button = this.root.querySelector('button[name="add"]');
      this.input = this.form && this.form.querySelector('input[name="id"]');
      if (!this.button || !this.input) return;

      this.textSpan = this.button.querySelector('.product-form-button-text');
      this.successSpan = this.button.querySelector('.product-added-success');
      this.successHTML = this.successSpan ? this.successSpan.innerHTML : null;

      try {
        const data = JSON.parse(this.querySelector('[data-veya-offer-variants]').textContent);
        this.variants = data.variants || {};
        this.soldOut = this.normalize(data.sold_out || '').trim();
        // Plain labels the app JS may write back; these are ours to replace.
        this.generic = new Set(
          [data.generic, window.variantStrings && window.variantStrings.addToCart]
            .filter(Boolean)
            .map((text) => this.normalize(text).trim())
        );
      } catch (error) {
        return;
      }
      // Compare markup the way the browser serialises it, so a restore never loops.
      this.labels = new Set();
      Object.values(this.variants).forEach((variant) => {
        variant.label = this.normalize(variant.label);
        this.labels.add(variant.label);
      });

      // The Atlas add-to-cart wrapper has an inline 46px height; let it fit the lines below.
      const wrapper = this.root.parentElement;
      if (wrapper && wrapper.style && wrapper.style.height) wrapper.style.height = 'auto';

      this.sync = this.sync.bind(this);
      this.restore = this.restore.bind(this);

      // updateVariantInput() sets input[name=id] and fires "change" on it; the
      // document listener also catches the radios (they sit outside the form).
      this.form.addEventListener('change', this.sync);
      document.addEventListener('change', this.sync);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.variantChange, this.sync);
      }

      this.observer = new MutationObserver(this.restore);
      this.observer.observe(this.button, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.watchSubtotal();
      this.sync();
    }

    normalize(html) {
      const template = document.createElement('template');
      template.innerHTML = html;
      return template.innerHTML;
    }

    sync() {
      // Read on the next frame so the picker JS has already updated the input.
      requestAnimationFrame(() => {
        this.variant = this.variants[String(this.input.value)] || null;
        this.restore();
      });
    }

    restore() {
      if (!this.textSpan || !this.variant) return;

      // Atlas writes its strings into the "Added" span; put its own text back.
      if (this.successSpan && this.successHTML !== null && this.successSpan.innerHTML !== this.successHTML) {
        this.successSpan.innerHTML = this.successHTML;
      }

      const current = this.textSpan.innerHTML.trim();
      const priced = this.labels.has(current) || this.generic.has(current);
      const ours = current === '' || priced || current === this.soldOut;

      if (!this.variant.available) {
        // Sold out: only swap out an add-to-cart label, and leave the button disabled.
        if (this.soldOut && priced) this.textSpan.innerHTML = this.soldOut;
        return;
      }

      if (!ours) return;
      if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true' ||
          this.button.classList.contains('loading')) return;
      if (current !== this.variant.label) this.textSpan.innerHTML = this.variant.label;
    }

    watchSubtotal() {
      if (this.dataset.currency !== 'AED') return;
      const section = this.closest('[id^="MainProduct-"]') || document;
      this.subtotal = section.querySelector('.subtotal-text[id^="subtotal-"]');
      if (!this.subtotal) return;
      const trim = () => {
        const amount = this.subtotal.querySelector('.subtotal-amount');
        if (!amount) return;
        const text = amount.textContent;
        const trimmed = text.replace(/(\d)\.00(?!\d)/, '$1');
        if (trimmed !== text) amount.textContent = trimmed;
      };
      this.subtotalObserver = new MutationObserver(trim);
      this.subtotalObserver.observe(this.subtotal, { childList: true, characterData: true, subtree: true });
      trim();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.subtotalObserver) this.subtotalObserver.disconnect();
      if (this.form) this.form.removeEventListener('change', this.sync);
      document.removeEventListener('change', this.sync);
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }
  });
}
